import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Context
import { LanguageProvider } from "@/context/LanguageContext";

// Components
import InstallPrompt from "@/components/InstallPrompt";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import PricingPage from "@/pages/PricingPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import ClientDashboard from "@/pages/ClientDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import EmployerDashboard from "@/pages/EmployerDashboard";
import RideDetails from "@/pages/RideDetails";
import SalesPitchPage from "@/pages/SalesPitchPage";
import FreeTrialPage from "@/pages/FreeTrialPage";
import PublicTrackingPage from "@/pages/PublicTrackingPage";
import BusinessProposalPage from "@/pages/BusinessProposalPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import OwnerDashboard from "@/pages/OwnerDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// API instance with auth
export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // A failed sign-in is answered on the login form itself, not by a page reload
    if (error.response?.status === 401 && error.config?.url !== "/auth/login") {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    if (error.response?.status === 403 && error.response?.data?.detail === "PASSWORD_CHANGE_REQUIRED"
        && window.location.pathname !== "/change-password") {
      window.location.href = "/change-password";
    }
    if (error.response?.status === 403 && error.response?.data?.detail === "ACCOUNT_SUSPENDED") {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login?suspended=1";
    }
    return Promise.reject(error);
  }
);

// Protected Route
const ProtectedRoute = ({ children, allowedRoles, skipPasswordCheck = false }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password && !skipPasswordCheck) {
    return <Navigate to="/change-password" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const dashboardRoutes = {
      client: "/client",
      driver: "/driver",
      employer: "/employer"
    };
    return <Navigate to={dashboardRoutes[user.role]} replace />;
  }
  
  return children;
};

// Subscription Protected Route - for employers only
const SubscriptionProtectedRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || user.role !== "employer") {
        setCheckingSubscription(false);
        setHasSubscription(true); // Non-employers don't need subscription
        return;
      }

      try {
        const response = await apiClient.get("/subscriptions/check");
        setHasSubscription(response.data.has_subscription);
      } catch (error) {
        console.error("Subscription check failed:", error);
        // On error, allow access (don't block due to network issues)
        setHasSubscription(true);
      } finally {
        setCheckingSubscription(false);
      }
    };

    if (user) {
      checkSubscription();
    } else {
      setCheckingSubscription(false);
    }
  }, [user]);
  
  if (isLoading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  // Allow access to dashboard - subscription banner will prompt to subscribe
  // Removed hard redirect to allow employers to explore the dashboard first
  return children;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      const savedUser = localStorage.getItem("user");
      
      if (token && savedUser) {
        try {
          const response = await apiClient.get("/auth/me");
          setUser(response.data);
          localStorage.setItem("user", JSON.stringify(response.data));
        } catch (error) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
      setIsLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await apiClient.post("/auth/login", { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await apiClient.post("/auth/register", data);
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Logged out successfully");
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Home redirect
const HomeRedirect = () => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  const dashboardRoutes = {
    client: "/client",
    driver: "/driver",
    employer: "/employer"
  };
  
  return <Navigate to={dashboardRoutes[user.role]} replace />;
};

function App() {
  return (
    <BrowserRouter>
    <LanguageProvider>
      <AuthProvider>
        <div className="App min-h-screen">
          <Routes>
            {/* Business Proposal is now the front page */}
            <Route path="/" element={<BusinessProposalPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/sales" element={<SalesPitchPage />} />
            <Route path="/free-trial" element={<FreeTrialPage />} />
            <Route path="/change-password" element={
              <ProtectedRoute skipPasswordCheck>
                <ChangePasswordPage />
              </ProtectedRoute>
            } />
            
            <Route path="/client" element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/driver" element={
              <ProtectedRoute allowedRoles={["driver"]}>
                <DriverDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/employer" element={
              <SubscriptionProtectedRoute>
                <EmployerDashboard />
              </SubscriptionProtectedRoute>
            } />
            
            <Route path="/pricing" element={<PricingPage />} />
            
            <Route path="/owner" element={
              <ProtectedRoute allowedRoles={["employer"]}>
                <OwnerDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/subscriptions" element={
              <ProtectedRoute allowedRoles={["employer"]}>
                <SubscriptionsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/ride/:rideId" element={
              <ProtectedRoute>
                <RideDetails />
              </ProtectedRoute>
            } />
            
            {/* Public tracking page - no auth required */}
            <Route path="/track/:shareToken" element={<PublicTrackingPage />} />
          </Routes>
          <Toaster position="top-right" richColors />
          <InstallPrompt />
        </div>
      </AuthProvider>
    </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
